import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  FieldValue,
  FormErrors,
  FormState,
  Transition,
} from "../../../shared/types/workflow/workflow-form.types";
import type { Field } from "../../../shared/types/workflow/workflow-form.types";
import {
  getCommentDefaultValue,
  getConclusionSelectDefaultValue,
  getFileUploadDefaultValue,
  getSelectDefaultValue,
  getTextAreaDefaultValue,
} from "../utils";
import { validateFieldValue } from "../utils/validators";

/**
 * Returns the appropriate default value according to the field type
 */
function getDefaultValueForField(field: Field): FieldValue {
  if (field.default_value !== undefined) {
    return field.default_value as FieldValue;
  }

  switch (field.type) {
    case "textarea": {
      return getTextAreaDefaultValue();
    }

    case "comment": {
      return getCommentDefaultValue();
    }

    case "select": {
      return getSelectDefaultValue(field);
    }

    case "file_upload": {
      return getFileUploadDefaultValue();
    }

    case "conclusion_select": {
      return getConclusionSelectDefaultValue();
    }

    default: {
      return "";
    }
  }
}

export function useWorkflowForm(
  schema: Transition,
  initialValues?: Partial<FormState>,
) {
  const { t } = useTranslation();

  // Compute initial values from schema and merge with initialValues
  const formInitialValues: FormState = useMemo(() => {
    const defaultValues = (schema?.action?.fields ?? []).reduce(
      (acc: FormState, f: Field) => {
        acc[f.name] = getDefaultValueForField(f);
        return acc;
      },
      {} as FormState,
    );
    return {
      ...defaultValues,
      ...Object.fromEntries(
        Object.entries(initialValues ?? {}).filter(
          (entry): entry is [string, FieldValue] => entry[1] !== undefined,
        ),
      ),
    };
  }, [schema, initialValues]);

  const [formState, setFormState] = useState<FormState>(formInitialValues);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Keep a ref to the latest form state to avoid stale closures in validation
  const formStateRef = useRef(formState);
  useEffect(() => {
    formStateRef.current = formState;
  }, [formState]);

  const updateField = useCallback((name: string, value: FieldValue) => {
    setFormState((prev) => ({ ...prev, [name]: value }));
  }, []);

  const touchField = useCallback((name: string) => {
    setTouched((prev) => new Set(prev).add(name));
  }, []);

  /**
   * Validates a single field. Sets error state only if the field is touched.
   * Removes error if not touched.
   * @param name - Field name to validate
   * @param touchedSet - Optional touched set to use instead of state
   * @param fieldValue - Optional field value to validate (if not provided, reads from latest state via ref)
   */
  const validateField = useCallback(
    async (name: string, touchedSet?: Set<string>, fieldValue?: FieldValue) => {
      const field = schema?.action?.fields?.find((f) => f.name === name);
      if (!field) return true;

      // Use provided fieldValue or fall back to latest state from ref
      const valueToValidate =
        fieldValue !== undefined ? fieldValue : formStateRef.current[name];

      // Validate field using dedicated validators with translation
      const error = validateFieldValue({ field, value: valueToValidate, t });

      const useTouched = touchedSet ?? touched;
      setErrors((prev) => {
        if (useTouched.has(name)) {
          return { ...prev, [name]: error };
        }
        const { [name]: _removed, ...rest } = prev;
        return rest;
      });

      return !error;
    },
    [schema, touched, t],
  );

  const validateForm = useCallback(async () => {
    const allFieldNames = (schema?.action?.fields ?? []).map(
      (field) => field.name,
    );
    // Synchronously mark all fields as touched
    const allTouched = new Set<string>([...touched, ...allFieldNames]);
    setTouched(allTouched);

    let valid = true;
    // Validate using the local allTouched set
    for (const name of allFieldNames) {
      const fieldValid = await validateField(name, allTouched);
      if (!fieldValid) valid = false;
    }
    return valid;
  }, [schema, validateField, touched]);

  const resetForm = useCallback(() => {
    setFormState(formInitialValues);
    setErrors({});
    setTouched(new Set());
  }, [formInitialValues]);

  return {
    formState,
    errors,
    touched,
    updateField,
    touchField,
    validateField,
    validateForm,
    resetForm,
    isSubmitting,
    setIsSubmitting,
  };
}
